import * as React from "react";
import { Link } from "react-router-dom";
import { Content, Portal, Root, Trigger } from "@radix-ui/react-popover";
import { ChevronRight, LayoutGrid } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { Button } from "components/common/buttons/Button";
import {
  PROJECTS_HOME_NAV_LABELS,
  PROJECT_NAV_SECTION_LABELS,
  PROJECT_STEP_STATUS_LABELS,
  type ProjectLinkId,
  type ProjectStepId,
  type ProjectStepStatus,
} from "config/navigation";
import { cn } from "utils/cn";
import { useAppSidebarLayout } from "../appSidebarLayout";
import {
  CollapsedTooltip,
  SidebarIcon,
  SidebarRowLink,
  SidebarSectionLabel,
  navActiveClassName,
  sidebarFocusRingClassName,
  sidebarRowClassName,
} from "../SidebarRow";
import { StepStatusDot, StepStatusIndicator, StudioLockPill } from "./StepIndicators";

export interface ProjectNavItemModel {
  id: string;
  label: string;
  icon: LucideIcon;
  href: string;
  active: boolean;
  /** Hover-prefetch handlers for the page's lazy chunk. */
  prefetch?: Record<string, unknown>;
}

export interface ProjectNavStepModel {
  id: ProjectStepId;
  label: string;
  icon: LucideIcon;
  status: ProjectStepStatus;
  /** Writer project on a Studio step. */
  locked: boolean;
  /** The current page belongs to this step. */
  isCurrent: boolean;
  lockDescription: string;
  items: ProjectNavItemModel[];
}

export interface ProjectNavLinkModel {
  id: ProjectLinkId;
  label: string;
  icon: LucideIcon;
  href: string;
  active: boolean;
}

export interface ProjectNavProps {
  steps: ProjectNavStepModel[];
  links: ProjectNavLinkModel[];
  openStepId: ProjectStepId | null;
  upsellStepId: ProjectStepId | null;
  onToggleStep: (stepId: ProjectStepId) => void;
  onUpgrade: (stepId: ProjectStepId) => void;
  onItemClick: (stepId: ProjectStepId, itemId: string) => void;
  onLinkClick: (linkId: ProjectLinkId) => void;
  /** The workspace's project list, one tap away from any project page. */
  allProjectsHref: string;
  onAllProjectsClick: () => void;
}

const UpgradeButton = ({ onClick }: { onClick: () => void }) => (
  <Button type="button" color="brand" size="xs" onClick={onClick}>
    Upgrade to Studio
  </Button>
);

const StepItemLink = ({
  item,
  inFlyout,
  onClick,
}: {
  item: ProjectNavItemModel;
  inFlyout: boolean;
  onClick: () => void;
}) => (
  <Link
    to={item.href}
    onClick={onClick}
    aria-current={item.active ? "page" : undefined}
    {...(item.prefetch ?? {})}
    className={cn(
      "relative flex min-h-[30px] w-full items-center gap-2 rounded-[7px] py-1.5 pr-2 text-[13px] transition-colors",
      sidebarFocusRingClassName,
      inFlyout
        ? "pl-2.5 text-popover-foreground hover:bg-muted"
        : // The page icon lines up with the step's label above it.
          "pl-8 text-chrome-muted-foreground hover:bg-chrome-muted hover:text-chrome-foreground",
      item.active && navActiveClassName(inFlyout ? "popover" : "chrome")
    )}
  >
    <item.icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
    <span className="min-w-0 flex-1 truncate">{item.label}</span>
  </Link>
);

/** One production step, expanded sidebar: header row, items or the Studio explanation. */
const ExpandedStep = ({
  step,
  isOpen,
  isUpsellOpen,
  onToggle,
  onUpgrade,
  onItemClick,
}: {
  step: ProjectNavStepModel;
  isOpen: boolean;
  isUpsellOpen: boolean;
  onToggle: () => void;
  onUpgrade: () => void;
  onItemClick: (itemId: string) => void;
}) => {
  const panelId = React.useId();
  const expanded = step.locked ? isUpsellOpen : isOpen;

  return (
    <div data-step={step.id}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        className={cn(sidebarRowClassName({}), "gap-2 pr-1.5", step.isCurrent && "font-semibold")}
      >
        <SidebarIcon icon={step.icon} />
        <span className="min-w-0 flex-1 truncate" title={step.label}>
          {step.label}
        </span>
        {step.locked ? <StudioLockPill /> : <StepStatusIndicator status={step.status} />}
        <ChevronRight
          aria-hidden
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-chrome-muted-foreground transition-transform duration-150 motion-reduce:transition-none",
            expanded && "rotate-90"
          )}
        />
      </button>

      {step.locked ? (
        isUpsellOpen ? (
          <div
            id={panelId}
            className="mb-2 ml-9 mr-1.5 mt-0.5 rounded-lg border border-chrome-border bg-card p-2.5 text-xs leading-snug"
          >
            <p className="mb-2 text-chrome-muted-foreground">{step.lockDescription}</p>
            <UpgradeButton onClick={onUpgrade} />
          </div>
        ) : null
      ) : (
        <div
          id={panelId}
          role="group"
          aria-label={step.label}
          hidden={!isOpen}
          className="pb-1.5 pt-0.5"
        >
          {step.items.map((item) => (
            <StepItemLink
              key={item.id}
              item={item}
              inFlyout={false}
              onClick={() => onItemClick(item.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/** One production step, collapsed sidebar: icon with a flyout of its pages. */
const CollapsedStep = ({
  step,
  isFlyoutOpen,
  onFlyoutChange,
  onUpgrade,
  onItemClick,
}: {
  step: ProjectNavStepModel;
  isFlyoutOpen: boolean;
  onFlyoutChange: (open: boolean) => void;
  onUpgrade: () => void;
  onItemClick: (itemId: string) => void;
}) => {
  const statusText = step.locked ? "Studio" : PROJECT_STEP_STATUS_LABELS[step.status];

  return (
    <Root open={isFlyoutOpen} onOpenChange={onFlyoutChange}>
      <CollapsedTooltip label={`${step.label}, ${statusText}`}>
        <Trigger asChild>
          <button
            type="button"
            aria-label={`${step.label}, ${statusText}`}
            aria-expanded={isFlyoutOpen}
            data-step={step.id}
            className={cn(
              sidebarRowClassName({ collapsed: true, active: step.isCurrent }),
              "font-normal"
            )}
          >
            <SidebarIcon icon={step.icon} />
            <span className="absolute right-2 top-1.5">
              {step.locked ? <StudioLockPill iconOnly /> : <StepStatusDot status={step.status} />}
            </span>
          </button>
        </Trigger>
      </CollapsedTooltip>
      <Portal>
        <Content
          side="right"
          align="start"
          sideOffset={8}
          collisionPadding={12}
          aria-label={step.label}
          className="z-[200] min-w-[200px] rounded-[10px] border border-border bg-popover p-1.5 text-popover-foreground shadow-[0_12px_32px_rgb(0_0_0/0.2)] outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0"
        >
          <div className="flex items-center gap-2 px-2 pb-1.5 pt-1 text-[13px] font-semibold">
            <SidebarIcon icon={step.icon} />
            <span className="flex-1">{step.label}</span>
            {step.locked ? <StudioLockPill /> : <StepStatusIndicator status={step.status} />}
          </div>
          {step.locked ? (
            <div className="max-w-[240px] px-2 pb-1.5">
              <p className="mb-2 text-xs leading-snug text-muted-foreground">
                {step.label} comes with a Studio license.
              </p>
              <UpgradeButton onClick={onUpgrade} />
            </div>
          ) : (
            <div role="group" aria-label={step.label}>
              {step.items.map((item) => (
                <StepItemLink
                  key={item.id}
                  item={item}
                  inFlyout
                  onClick={() => onItemClick(item.id)}
                />
              ))}
            </div>
          )}
        </Content>
      </Portal>
    </Root>
  );
};

/**
 * Production steps (Script, Characters & Cast, Schedule, Budget) and the
 * project links. Navigation only: a step's Generate action lives on its
 * empty page. Stateless: the container decides which step is open.
 */
export const ProjectNav = ({
  steps,
  links,
  openStepId,
  upsellStepId,
  onToggleStep,
  onUpgrade,
  onItemClick,
  onLinkClick,
  allProjectsHref,
  onAllProjectsClick,
}: ProjectNavProps) => {
  const { collapsed } = useAppSidebarLayout();
  const [flyoutStepId, setFlyoutStepId] = React.useState<ProjectStepId | null>(null);

  React.useEffect(() => {
    if (!collapsed) setFlyoutStepId(null);
  }, [collapsed]);

  return (
    <>
      <div className="flex flex-col gap-px pt-1">
        <CollapsedTooltip label={PROJECTS_HOME_NAV_LABELS.allProjects}>
          <SidebarRowLink
            to={allProjectsHref}
            icon={<SidebarIcon icon={LayoutGrid} />}
            label={PROJECTS_HOME_NAV_LABELS.allProjects}
            onClick={onAllProjectsClick}
          />
        </CollapsedTooltip>
      </div>

      <SidebarSectionLabel>{PROJECT_NAV_SECTION_LABELS.steps}</SidebarSectionLabel>
      <div className="flex flex-col gap-px">
        {steps.map((step) =>
          collapsed ? (
            <CollapsedStep
              key={step.id}
              step={step}
              isFlyoutOpen={flyoutStepId === step.id}
              onFlyoutChange={(open) => setFlyoutStepId(open ? step.id : null)}
              onUpgrade={() => {
                setFlyoutStepId(null);
                onUpgrade(step.id);
              }}
              onItemClick={(itemId) => {
                setFlyoutStepId(null);
                onItemClick(step.id, itemId);
              }}
            />
          ) : (
            <ExpandedStep
              key={step.id}
              step={step}
              isOpen={openStepId === step.id}
              isUpsellOpen={upsellStepId === step.id}
              onToggle={() => onToggleStep(step.id)}
              onUpgrade={() => onUpgrade(step.id)}
              onItemClick={(itemId) => onItemClick(step.id, itemId)}
            />
          )
        )}
      </div>

      <SidebarSectionLabel>{PROJECT_NAV_SECTION_LABELS.links}</SidebarSectionLabel>
      <div className="flex flex-col gap-px">
        {links.map((link) => (
          <CollapsedTooltip key={link.id} label={link.label}>
            <SidebarRowLink
              to={link.href}
              icon={<SidebarIcon icon={link.icon} />}
              label={link.label}
              active={link.active}
              onClick={() => onLinkClick(link.id)}
            />
          </CollapsedTooltip>
        ))}
      </div>
    </>
  );
};
